import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getUserChats, createChat, deleteChat, updateChatTitle } from '../../services/chatService';
import { useAuth } from '../auth/AuthContext';
import { useCompany } from '../Company/CompanyContext';
import SettingsModal from '../settings/SettingsModal';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import LordIcon from '../common/LordIcon';
import autorenewIcon from '../../assets/icons/autorenew.json';
import search from '../../assets/icons/search.json';
import chatIcon from '../../assets/icons/chaticon.json';
import scorecardicon from '../../assets/icons/assignment.json';
import rocksicon from '../../assets/icons/heart.json';
import vtoicon from '../../assets/icons/view.json';
import acccharticon from '../../assets/icons/article.json';
import coreicon from '../../assets/icons/book.json';
import trash from '../../assets/icons/trash.json';

function NavbarSidebar({ isExpanded, hideTitle = false, isMobile = false, onCloseMobile }) {
  // Sidebar states
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creatingChat, setCreatingChat] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(null);
  const [showContextMenu, setShowContextMenu] = useState(null);
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [searchMode, setSearchMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [animationReady, setAnimationReady] = useState(false);
  const editTitleInputRef = React.useRef(null);
  
  // Navbar states
  const [settingsOpen, setSettingsOpen] = useState(false);
  
  const { chatId } = useParams();
  const { currentUser } = useAuth();
  const { companyInfo } = useCompany();
  const navigate = useNavigate();
  
  // Animation variants for list items
  const listVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05 + 0.2,
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1]
      }
    })
  };
  
  // Enable animations after components are rendered
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationReady(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);
  
  // Focus on title input when editing starts
  useEffect(() => {
    if (editingTitleId && editTitleInputRef.current) {
      editTitleInputRef.current.focus();
    }
  }, [editingTitleId]);
  
  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowContextMenu(null);
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);
  
  // Fetch user chats
  useEffect(() => {
    async function fetchChats() {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const userChats = await getUserChats(currentUser.uid);
        setChats(userChats);
        setError('');
      } catch (err) {
        console.error('Error fetching chats:', err);
        setError('Failed to load chats');
      } finally {
        setLoading(false);
      }
    }
    
    fetchChats();
  }, [currentUser]);
  
  // Check for newly created chats after navigation
  useEffect(() => {
    const checkForNewChat = () => {
      const justCreatedChatId = localStorage.getItem('justCreatedChatId');
      
      if (justCreatedChatId) {
        console.log('Detected newly created chat after navigation:', justCreatedChatId);
        refreshChats();
        localStorage.removeItem('justCreatedChatId');
      }
    };
    
    checkForNewChat();
    window.addEventListener('focus', checkForNewChat);
    return () => {
      window.removeEventListener('focus', checkForNewChat);
    };
  }, [currentUser]);
  
  // Listen for chat title updates from any component
  useEffect(() => {
    const handleTitleUpdate = (event) => {
      const { chatId: updatedChatId, newTitle: updatedTitle } = event.detail;
      
      setChats(prevChats => 
        prevChats.map(chat => 
          chat.id === updatedChatId 
            ? { ...chat, title: updatedTitle } 
            : chat
        )
      );
    };
    
    window.addEventListener('chat-title-updated', handleTitleUpdate);
    
    return () => {
      window.removeEventListener('chat-title-updated', handleTitleUpdate);
    };
  }, []);
  
  // Listen for new chat creation from other components
  useEffect(() => {
    const handleNewChat = (event) => {
      const { newChat } = event.detail;
      setChats(prevChats => [newChat, ...prevChats]);
    };
    
    window.addEventListener('new-chat-created', handleNewChat);
    
    return () => {
      window.removeEventListener('new-chat-created', handleNewChat);
    };
  }, []);

  // Handle open settings
  const handleOpenSettings = () => {
    setSettingsOpen(true);
  };
  
  const handleCloseSettings = () => {
    setSettingsOpen(false);
  };

  // Sidebar functions
  const handleCreateChat = async () => {
    if (creatingChat) return;
    
    try {
      setCreatingChat(true);
      const newChat = await createChat(currentUser.uid);
      setChats(prevChats => [newChat, ...prevChats]);
      
      // Set view to 'chat' when creating a new chat
      if (activeView !== 'chat') {
        setActiveView('chat');
        // Dispatch event to update the view in the ChatContainer
        const viewChangeEvent = new CustomEvent('chat-view-change', { 
          detail: { view: 'chat' }
        });
        window.dispatchEvent(viewChangeEvent);
      }
      
      // Close mobile sidebar if on mobile
      if (isMobile && onCloseMobile) {
        onCloseMobile();
      }
      
      navigate(`/chat/${newChat.id}`);
    } catch (err) {
      console.error('Error creating chat:', err);
      setError('Failed to create a new chat');
    } finally {
      setCreatingChat(false);
    }
  };
  
  const handleDeleteChat = async (chatIdToDelete) => {
    try {
      await deleteChat(chatIdToDelete);
      setChats(prevChats => prevChats.filter(chat => chat.id !== chatIdToDelete));
      
      if (chatIdToDelete === chatId) {
        navigate('/');
      }
      
      setShowConfirmDelete(null);
      setShowContextMenu(null);
    } catch (err) {
      console.error('Error deleting chat:', err);
      setError('Failed to delete chat');
    }
  };
  
  const handleEditTitle = (chat) => {
    setEditingTitleId(chat.id);
    setNewTitle(chat.title || 'New Chat');
    setShowContextMenu(null);
  };
  
  const handleSaveTitle = async (chatId) => {
    if (!newTitle.trim()) {
      const chat = chats.find(c => c.id === chatId);
      setNewTitle(chat?.title || 'New Chat');
      setEditingTitleId(null);
      return;
    }
    
    try {
      await updateChatTitle(chatId, newTitle.trim());
      setChats(prevChats => 
        prevChats.map(chat => 
          chat.id === chatId ? { ...chat, title: newTitle.trim() } : chat
        )
      );
      setEditingTitleId(null);
      
      const titleUpdateEvent = new CustomEvent('chat-title-updated', { 
        detail: {
          chatId,
          newTitle: newTitle.trim()
        }
      });
      window.dispatchEvent(titleUpdateEvent);
      
    } catch (err) {
      console.error('Error updating chat title:', err);
      setError('Failed to update chat title');
      setEditingTitleId(null);
    }
  };

  const refreshChats = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      const userChats = await getUserChats(currentUser.uid);
      setChats(userChats);
      setError('');
    } catch (err) {
      console.error('Error refreshing chats:', err);
      setError('Failed to refresh chats');
    } finally {
      setLoading(false);
    }
  };

  // Filter chats based on search
  const filteredChats = searchMode && searchQuery 
    ? chats.filter(chat => (chat.title || 'New Chat').toLowerCase().includes(searchQuery.toLowerCase()))
    : chats;

  // Views state for the navigation section - changing to document context management
  const [activeView, setActiveView] = useState('chat');
  const [contextDocuments, setContextDocuments] = useState({
    'scorecard': { active: false, label: 'Scorecard' },
    'vto': { active: false, label: 'VTO' },
    'rocks': { active: false, label: 'Rocks' },
    'accountability_chart': { active: false, label: 'A/C' },
    'core_process': { active: false, label: 'Core Process' },
    'chat': { active: true, label: 'Chat' }
  });
  
  // Handle document context selection
  const handleContextChange = (context) => {
    // Update active context
    const updatedContexts = {...contextDocuments};
    Object.keys(updatedContexts).forEach(key => {
      updatedContexts[key].active = (key === context);
    });
    setContextDocuments(updatedContexts);
    
    // Update view to match selected context
    setActiveView(context);
    
    // Dispatch event for ChatContainer to change view
    const viewChangeEvent = new CustomEvent('chat-view-change', { 
      detail: { view: context }
    });
    window.dispatchEvent(viewChangeEvent);
    
    // If switching to a non-chat view, navigate to base route to deselect the chat
    if (context !== 'chat' && chatId) {
      navigate('/');
    }
    
    // Close mobile sidebar if on mobile
    if (isMobile && onCloseMobile) {
      onCloseMobile();
    }
  };

  const containerClasses = isExpanded
    ? 'opacity-0 pointer-events-none'
    : 'opacity-100 pointer-events-auto';

  return (
    <div className={`flex flex-col h-full ${isExpanded ? '' : 'overflow-hidden'}`}>
      {/* Sidebar top section */}
      <div className={`flex items-center justify-between px-4 py-3 ${!hideTitle ? 'border-b border-orange-100' : ''}`}>
        {!hideTitle && (
            <div className="flex items-center">
        
            <h1 className="text-xl font-semibold text-orange-700">Chats</h1>
            </div>
        )}
        
              <div className={`flex space-x-2 ${hideTitle ? 'w-full justify-between' : ''}`}>
                <motion.button
                  onClick={refreshChats}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-50/50 text-orange-700 hover:bg-orange-100/50 hover:text-orange-600"
                  style={{ transitionProperty: 'background-color, color' }}
                  title="Refresh Chats"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <LordIcon 
                    icon={autorenewIcon} 
                    size={24} 
                    trigger="hover"
                    colors="primary:#d45e09"
                  />
                </motion.button>
                
                <motion.button
                  onClick={() => setSearchMode(true)}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-50/50 text-orange-700 hover:bg-orange-100/50 hover:text-orange-600"
                  style={{ transitionProperty: 'background-color, color' }}
                  title="Search Chats"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                 <LordIcon 
                    icon={search} 
                    size={24} 
                    trigger="hover"
                    colors="primary:#d45e09"
                  />
                </motion.button>
                
                <motion.button
                  onClick={handleCreateChat}
                  disabled={creatingChat}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-orange-500 text-white hover:bg-orange-600 shadow-sm hover:shadow"
                  style={{ transitionProperty: 'background-color, color, box-shadow' }}
                  title="New Chat"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </motion.button>
              </div>
      </div>
      
      {/* Main content - split into two sections */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Chat list section - takes up top half */}
        <div className="flex-1 overflow-auto max-h-[50%] scrollbar-thin">
          {/* Search bar */}
          {searchMode && (
            <div className="flex items-center">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search chats..."
                className="flex-1 px-3 py-2 border border-orange-100/50 rounded-l-xl focus:outline-none focus:ring-1 focus:ring-orange-300 text-sm bg-white/70 backdrop-blur-sm"
                autoFocus
              />
              <button
                onClick={() => {
                  setSearchMode(false);
                  setSearchQuery('');
                }}
                className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-2 rounded-r-xl transition-colors duration-300"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        
          {/* Chat list */}
          <div className="px-2">
            {/* Chat list items */}
          {loading ? (
            <div className="flex flex-col items-center justify-center h-32">
              <div className="animate-pulse-slow rounded-full h-10 w-10 border-t-2 border-b-2 border-orange-500"></div>
              <p className="mt-3 text-sm text-orange-700">Loading chats...</p>
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-500">
              <p>{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                className="mt-2 px-4 py-2 bg-red-100/70 text-red-700 rounded-xl text-sm hover:bg-red-200/70 transition-colors"
              >
                <LordIcon 
                  icon={autorenewIcon} 
                  size={20} 
                  trigger="hover"
                  colors="primary:#b91c1c"
                  className="mr-1"
                />
                Retry
              </button>
            </div>
          ) : filteredChats.length === 0 ? (
            <motion.div 
              className="flex flex-col items-center justify-center h-32 text-center px-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.5 }}
            >
              <p className="text-orange-700 text-sm mb-4">No chats found. Start a new conversation!</p>
              <motion.button
                onClick={handleCreateChat}
                disabled={creatingChat}
                className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl shadow-sm hover:shadow transition-all duration-300 text-sm flex items-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {creatingChat ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    New Chat
                  </>
                )}
              </motion.button>
            </motion.div>
          ) : (
            <motion.ul 
              className="space-y-2"
              initial="hidden"
              animate={animationReady ? "visible" : "hidden"}
              variants={listVariants}
            >
              {filteredChats.map((chat, index) => (
                <motion.li 
                  key={chat.id} 
                  className="relative"
                  variants={itemVariants}
                  custom={index}
                >
                  {editingTitleId === chat.id ? (
                    <div className="flex items-center p-3 pl-4 rounded-xl bg-white/30 backdrop-blur-sm shadow-sm">
                      <div className="flex flex-1 items-center">
                        <input
                          ref={editTitleInputRef}
                          type="text"
                          value={newTitle}
                          onChange={(e) => setNewTitle(e.target.value)}
                          onBlur={() => handleSaveTitle(chat.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveTitle(chat.id);
                            } else if (e.key === 'Escape') {
                              setEditingTitleId(null);
                              setNewTitle(chat.title || 'New Chat');
                            }
                          }}
                          className="w-full px-3 py-1.5 border border-orange-200/50 rounded-lg focus:outline-none focus:ring-1 focus:ring-orange-300 text-sm"
                          placeholder="Chat title"
                        />
                        <div className="flex ml-2 flex-shrink-0">
                          <button
                            onClick={() => handleSaveTitle(chat.id)}
                            className="p-2 mr-1 text-orange-600 hover:bg-orange-50/50 rounded-full transition-all duration-300"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              setEditingTitleId(null);
                              setNewTitle(chat.title || 'New Chat');
                            }}
                            className="p-2 text-gray-500 hover:bg-gray-100/50 rounded-full transition-all duration-300"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <motion.div
                      whileHover={{ x: 3 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Link
                        to={`/chat/${chat.id}`}
                        className={`
                          group flex items-center p-3 pl-4 pr-3 
                          rounded-xl transition-all duration-300 
                          ${chatId === chat.id 
                            ? 'bg-white/40 backdrop-blur-sm shadow-sm border-l-4 border-orange-500' 
                            : 'bg-transparent hover:bg-white/20 hover:shadow-sm'} 
                          mb-3
                        `}
                        onClick={() => {
                            // Set the view to 'chat' when clicking on a chat
                            if (activeView !== 'chat') {
                              setActiveView('chat');
                              // Dispatch event to update the view in the ChatContainer
                              const viewChangeEvent = new CustomEvent('chat-view-change', { 
                                detail: { view: 'chat' }
                              });
                              window.dispatchEvent(viewChangeEvent);
                            }
                            
                          // Close mobile sidebar when selecting a chat on mobile
                          if (isMobile && onCloseMobile) {
                            onCloseMobile();
                          }
                        }}
                      >
                        <div className="flex-1 overflow-hidden flex items-center">
                          <span className={`
                            truncate font-medium text-sm
                            ${chatId === chat.id ? 'text-orange-700' : 'text-orange-800'}
                            transition-all duration-300 group-hover:text-orange-600
                          `}>
                            {chat.title || 'New Chat'}
                          </span>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowContextMenu(chat.id);
                            }}
                            className="ml-auto p-2 rounded-full text-orange-700/70 hover:text-orange-600 hover:bg-orange-50/50 transition-all duration-300 flex-shrink-0"
                            title="Menu"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
                            </svg>
                          </button>
                        </div>
                      </Link>
                    </motion.div>
                  )}
                  
                  {/* Context menu */}
                  <AnimatePresence>
                    {showContextMenu === chat.id && (
                      <motion.div 
                        className="absolute right-0 top-full mt-1 w-48 bg-white/80 backdrop-blur-md rounded-xl shadow-sm border border-orange-100/50 z-10 py-1"
                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                        transition={{ duration: 0.2 }}
                      >
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEditTitle(chat);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-orange-700 hover:bg-orange-50/50 hover:text-orange-600 flex items-center transition-colors"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                          Edit Title
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowConfirmDelete(chat.id);
                            setShowContextMenu(null);
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50/50 flex items-center transition-colors"
                        >
                          <LordIcon 
                            icon={trash} 
                            size={16} 
                            trigger="hover"
                            colors="primary:#ef4444"
                            className="mr-2"
                          />
                          Delete
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  {/* Delete confirmation modal */}
                  <AnimatePresence>
                    {showConfirmDelete === chat.id && (
                      <motion.div 
                        className="absolute right-0 top-full mt-1 w-48 p-3 bg-white/80 backdrop-blur-md rounded-xl shadow-sm border border-orange-100/50 z-10"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                      >
                        <p className="text-sm text-orange-700 mb-2">Delete this chat?</p>
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowConfirmDelete(null);
                            }}
                            className="px-3 py-1 text-xs text-gray-600 hover:bg-gray-100/50 rounded-lg transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteChat(chat.id);
                            }}
                            className="px-3 py-1 text-xs bg-red-500 text-white hover:bg-red-600 rounded-lg transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.li>
              ))}
            </motion.ul>
          )}
        </div>
        </div>
        
        {/* Navigation section separator - dotted line */}
        <div className="mx-3 border-t border-orange-200 border-dashed my-2"></div>
        
        {/* Context Documents section - bottom half - MOVED DOWN */}
        <div className="px-3 overflow-auto scrollbar-thin">
          <h3 className="text-sm font-medium text-orange-700 opacity-80 mb-2 px-2">Documents</h3>
          <motion.ul 
            className="space-y-2"
            initial="hidden"
            animate={animationReady ? "visible" : "hidden"}
            variants={listVariants}
          >
            <motion.li 
              custom={0} 
              variants={itemVariants}
              className="relative"
            >
              <motion.div
                whileHover={{ x: 3 }}
                transition={{ duration: 0.2 }}
              >
                <div
                  className={`
                    group flex items-center p-3 pl-4 pr-3 
                    rounded-xl transition-all duration-300 
                    ${contextDocuments.scorecard.active 
                      ? 'bg-white/40 backdrop-blur-sm shadow-sm border-l-4 border-orange-500' 
                      : 'bg-transparent hover:bg-white/20 hover:shadow-sm'} 
                    mb-3 cursor-pointer
                  `}
                  onClick={() => handleContextChange('scorecard')}
                >
                  <div className="flex-1 overflow-hidden flex items-center">
                  <LordIcon 
                    icon={scorecardicon} 
                    size={24} 
                    trigger="hover"
                    colors="primary:#d45e09"
                  />
                    <span className={`
                    ml-2    
                      truncate font-medium text-sm
                      ${contextDocuments.scorecard.active ? 'text-orange-700' : 'text-orange-800'}
                      transition-all duration-300 group-hover:text-orange-600
                    `}>
                      Scorecard
                    </span>
                  </div>
                </div>
              </motion.div>
            </motion.li>
            
            <motion.li 
              custom={1} 
              variants={itemVariants}
              className="relative"
            >
              <motion.div
                whileHover={{ x: 3 }}
                transition={{ duration: 0.2 }}
              >
                <div
                  className={`
                    group flex items-center p-3 pl-4 pr-3 
                    rounded-xl transition-all duration-300 
                    ${contextDocuments.vto.active 
                      ? 'bg-white/40 backdrop-blur-sm shadow-sm border-l-4 border-orange-500' 
                      : 'bg-transparent hover:bg-white/20 hover:shadow-sm'} 
                    mb-3 cursor-pointer
                  `}
                  onClick={() => handleContextChange('vto')}
                >
                  <div className="flex-1 overflow-hidden flex items-center">
                  <LordIcon 
                    icon={vtoicon} 
                    size={24} 
                    trigger="hover"
                    colors="primary:#d45e09"
                  />
                    <span className={`
                    ml-2    
                      truncate font-medium text-sm
                      ${contextDocuments.vto.active ? 'text-orange-700' : 'text-orange-800'}
                      transition-all duration-300 group-hover:text-orange-600
                    `}>
                      VTO
                    </span>
                  </div>
                </div>
              </motion.div>
            </motion.li>
            
            <motion.li 
              custom={2} 
              variants={itemVariants}
              className="relative"
            >
              <motion.div
                whileHover={{ x: 3 }}
                transition={{ duration: 0.2 }}
              >
                <div
                  className={`
                    group flex items-center p-3 pl-4 pr-3 
                    rounded-xl transition-all duration-300 
                    ${contextDocuments.rocks.active 
                      ? 'bg-white/40 backdrop-blur-sm shadow-sm border-l-4 border-orange-500' 
                      : 'bg-transparent hover:bg-white/20 hover:shadow-sm'} 
                    mb-3 cursor-pointer
                  `}
                  onClick={() => handleContextChange('rocks')}
                >
                  <div className="flex-1 overflow-hidden flex items-center">
                  <LordIcon 
                    icon={rocksicon} 
                    size={24} 
                    trigger="hover"
                    colors="primary:#d45e09"
                  />
                    <span className={`
                    ml-2    
                      truncate font-medium text-sm
                      ${contextDocuments.rocks.active ? 'text-orange-700' : 'text-orange-800'}
                      transition-all duration-300 group-hover:text-orange-600
                    `}>
                      Rocks
                    </span>
                  </div>
                </div>
              </motion.div>
            </motion.li>
            
            <motion.li 
              custom={3} 
              variants={itemVariants}
              className="relative"
            >
              <motion.div
                whileHover={{ x: 3 }}
                transition={{ duration: 0.2 }}
              >
                <div
                  className={`
                    group flex items-center p-3 pl-4 pr-3 
                    rounded-xl transition-all duration-300 
                    ${contextDocuments.accountability_chart.active 
                      ? 'bg-white/40 backdrop-blur-sm shadow-sm border-l-4 border-orange-500' 
                      : 'bg-transparent hover:bg-white/20 hover:shadow-sm'} 
                    mb-3 cursor-pointer
                  `}
                  onClick={() => handleContextChange('accountability_chart')}
                >
                  <div className="flex-1 overflow-hidden flex items-center">
                  <LordIcon 
                    icon={acccharticon} 
                    size={24} 
                    trigger="hover"
                    colors="primary:#d45e09"
                  />
                    <span className={`
                    ml-2    
                      truncate font-medium text-sm
                      ${contextDocuments.accountability_chart.active ? 'text-orange-700' : 'text-orange-800'}
                      transition-all duration-300 group-hover:text-orange-600
                    `}>
                      A/C
                    </span>
                  </div>
                </div>
              </motion.div>
            </motion.li>
            
            <motion.li 
              custom={4} 
              variants={itemVariants}
              className="relative"
            >
              <motion.div
                whileHover={{ x: 3 }}
                transition={{ duration: 0.2 }}
              >
                <div
                  className={`
                    group flex items-center p-3 pl-4 pr-3 
                    rounded-xl transition-all duration-300 
                    ${contextDocuments.core_process.active 
                      ? 'bg-white/40 backdrop-blur-sm shadow-sm border-l-4 border-orange-500' 
                      : 'bg-transparent hover:bg-white/20 hover:shadow-sm'} 
                    mb-3 cursor-pointer
                  `}
                  onClick={() => handleContextChange('core_process')}
                >
                  <div className="flex-1 overflow-hidden flex items-center">
                  <LordIcon 
                    icon={coreicon} 
                    size={24} 
                    trigger="hover"
                    colors="primary:#d45e09"
                  />
                   
                    <span className={`
                    ml-2    
                      truncate font-medium text-sm
                      ${contextDocuments.core_process.active ? 'text-orange-700' : 'text-orange-800'}
                      transition-all duration-300 group-hover:text-orange-600
                    `}>
                      Core Process
                    </span>
                  </div>
                </div>
              </motion.div>
            </motion.li>
          </motion.ul>
        </div>
      </div>

      {/* Bottom buttons */}
      <div className="px-4 py-3 border-t border-orange-100">
        {/* ... existing bottom buttons ... */}
      </div>
      
      {/* Settings Modal */}
      {settingsOpen && (
        <SettingsModal onClose={handleCloseSettings} />
      )}
    </div>
  );
}

export default NavbarSidebar; 

/* Custom scrollbar styling */
/* Add this at the end of the file */
/* This ensures that we won't lose the styles if the file gets edited elsewhere */

const style = document.createElement('style');
style.textContent = `
  /* Custom scrollbar styling */
  .scrollbar-thin::-webkit-scrollbar {
    width: 5px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-track {
    background: rgba(255, 237, 213, 0.1);
    border-radius: 10px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb {
    background: rgba(249, 115, 22, 0.2);
    border-radius: 10px;
  }
  
  .scrollbar-thin::-webkit-scrollbar-thumb:hover {
    background: rgba(249, 115, 22, 0.4);
  }
  
  /* For Firefox */
  .scrollbar-thin {
    scrollbar-width: thin;
    scrollbar-color: rgba(249, 115, 22, 0.2) rgba(255, 237, 213, 0.1);
  }
`;
document.head.appendChild(style); 